import React from "react";
import { WidgetWrapper } from "../../../../components/WidgetWrapper/WidgetWrapper";
import { EditField } from "../../../../components/EditPanel/types";
import { useWidgetEditable } from "../../../../hooks/useWidgetEditable";
import { ConciergeHomeLogoState } from "./types";

const defaultState: ConciergeHomeLogoState = {
  logoIcon: "default",
  logoWidth: 200,
  logoMaxHeight: 80,
  logoOpacity: 1,
  marginBottom: 0,
  mobile_logoWidth: 140,
  mobile_marginBottom: 0,
};

const editFields: EditField[] = [
  {
    key: "logoIcon",
    label: "Logo Image",
    type: "icon",
  },
  {
    key: "logoWidth",
    label: "Logo Width",
    type: "number",
    min: 60,
    max: 400,
    step: 10,
    responsive: true,
  },
  {
    key: "logoMaxHeight",
    label: "Max Height",
    type: "number",
    min: 30,
    max: 200,
    step: 10,
  },
  {
    key: "logoOpacity",
    label: "Opacity",
    type: "number",
    min: 0.1,
    max: 1,
    step: 0.1,
  },
  {
    key: "marginBottom",
    label: "Spacing Below",
    type: "number",
    min: 0,
    max: 96,
    step: 4,
    responsive: true,
  },
];

interface ConciergeHomeLogoWidgetProps {
  widgetId?: string;
  displayName?: string;
  initialState?: Partial<ConciergeHomeLogoState>;
  logoUrl?: string;
}

export const ConciergeHomeLogoWidget: React.FC<
  ConciergeHomeLogoWidgetProps
> = ({
  widgetId = "concierge-home-logo",
  displayName = "Brand Logo",
  initialState = {},
  logoUrl,
}) => {
  const {
    state,
    isLoading,
    isSaving,
    handleEditChange,
    handleSave,
    themeColors,
    themeIcons,
    canSave,
  } = useWidgetEditable<ConciergeHomeLogoState>(
    widgetId,
    { ...defaultState, ...initialState },
    editFields
  );

  // Resolve logo source: use theme icon if set, otherwise fall back to the brand logo URL prop
  const customIcon =
    state.logoIcon && state.logoIcon !== "default"
      ? themeIcons[state.logoIcon]
      : null;
  const resolvedLogoUrl = customIcon ? customIcon.url : logoUrl;

  // A URL is not evidence that an image exists. `getBrandLogo(brandId)` BUILDS
  // `media.brandclub.com/b/428x428/<brandId>.jpg` from the id alone — nothing
  // uploads it, so for any brand without that asset (every self-serve brand)
  // the guard below can never fire: the URL is always a non-empty string, and
  // the shopper gets the browser's broken-image icon under the assistant on
  // every page. Failing to the empty state covers every reason it can 404 —
  // never uploaded, CDN rule, hotlink protection — without a HEAD round-trip.
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => setFailed(false), [resolvedLogoUrl]);

  if (isLoading || !resolvedLogoUrl || failed) {
    return <></>;
  }

  const uniqueClass = `logo-${widgetId || "default"}`;
  const wrapperClass = `logo-wrap-${widgetId || "default"}`;
  const mobileMarginBottom =
    (state as any).mobile_marginBottom ?? state.marginBottom;
  const mobileLogoWidth = (state as any).mobile_logoWidth ?? state.logoWidth;

  const stylesCss = `
    .${wrapperClass} {
      margin-bottom: ${state.marginBottom}px;
    }
    .${uniqueClass} img {
      width: ${state.logoWidth}px;
    }
    @media (max-width: 767px) {
      .${wrapperClass} {
        margin-bottom: ${mobileMarginBottom}px !important;
      }
      .${uniqueClass} img {
        width: ${mobileLogoWidth}px !important;
      }
    }
  `.trim();

  const content = (
    <>
      <style>{stylesCss}</style>
      <div
        className={`${wrapperClass} ${uniqueClass}`}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
        }}
      >
        <img
          src={resolvedLogoUrl}
          onError={() => setFailed(true)}
          alt={customIcon ? customIcon.name : "Brand logo"}
          style={{
            maxHeight: state.logoMaxHeight,
            opacity: state.logoOpacity,
            objectFit: "contain",
          }}
        />
      </div>
    </>
  );

  return (
    <WidgetWrapper
      widgetId={widgetId}
      displayName={displayName}
      editFields={editFields}
      editValues={state}
      onEditChange={handleEditChange}
      onSave={canSave ? handleSave : undefined}
      isSaving={isSaving}
      themeColors={themeColors}
      themeIcons={themeIcons}
    >
      {content}
    </WidgetWrapper>
  );
};
